import { useTranslation } from "react-i18next"

import type { SourceDescriptor, SourceSettings } from "@/api/filth-source-control-api"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { bytesToMb, sourceControlKey as key, mbToBytes } from "./filth-source-control-utils"

export function FilthSourceSettingsFields({
  value,
  onChange,
  descriptor,
  kind,
  editing = false,
}: {
  value: SourceSettings
  onChange: (value: SourceSettings) => void
  descriptor: SourceDescriptor
  kind: string
  editing?: boolean
}) {
  const { t } = useTranslation()
  const modes = descriptor.supported_acl_modes.filter((v) => ["posix", "ntfs_manifest"].includes(v))
  return (
    <div className="space-y-4">
      <Label className="grid gap-2">
        {t(`${key}.fileTypes`)}
        <Input
          value={value.include_extensions.join(", ")}
          onChange={(e) =>
            onChange({
              ...value,
              include_extensions: e.target.value
                .split(",")
                .map((v) => v.trim().toLowerCase())
                .filter(Boolean),
            })
          }
        />
      </Label>
      <Label className="grid gap-2">
        {t(`${key}.maxSize`)}
        <Input
          max={bytesToMb(descriptor.ceilings.max_file_bytes)}
          min={0}
          step="any"
          type="number"
          value={bytesToMb(value.max_file_bytes)}
          onChange={(e) =>
            onChange({ ...value, max_file_bytes: mbToBytes(Number(e.target.value)) })
          }
        />
        <span className="text-muted-foreground text-xs">
          {t(`${key}.sizeLimit`, { count: bytesToMb(descriptor.ceilings.max_file_bytes) })}
        </span>
      </Label>
      <Label className="grid gap-2">
        {t(`${key}.interval`)}
        <Input
          min={descriptor.ceilings.min_interval_seconds}
          placeholder={t(`${key}.manual`)}
          type="number"
          value={value.interval_seconds ?? ""}
          onChange={(e) =>
            onChange({
              ...value,
              interval_seconds: e.target.value === "" ? null : Number(e.target.value),
            })
          }
        />
        <span className="text-muted-foreground text-xs">
          {t(`${key}.intervalLimit`, { count: descriptor.ceilings.min_interval_seconds })}
        </span>
      </Label>
      <Label className="grid gap-2">
        {t(`${key}.aclMode`)}
        <select
          aria-label={t(`${key}.aclMode`)}
          className="bg-background rounded-md border p-2 text-sm"
          disabled={editing || kind === "sharepoint"}
          value={value.acl_mode}
          onChange={(e) => onChange({ ...value, acl_mode: e.target.value })}
        >
          {(editing || kind === "sharepoint" ? [value.acl_mode] : modes).map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </select>
      </Label>
    </div>
  )
}
